// API Status Component
// Displays API configuration status and health information

import React, { useState, useEffect } from 'react';
import { ApiHealthService } from '../../services/api';
import { getConfigurationStatus } from '../../config/api';
import './ApiStatus.css';

interface ApiStatusProps {
  showDetails?: boolean;
  className?: string;
  variant?: 'full' | 'pill' | 'minimal' | 'contextual';
  position?: 'header' | 'inline' | 'floating';
}

interface HealthStatus {
  healthy: boolean;
  configured: boolean;
  message: string;
  timestamp: string;
}

const ApiStatus: React.FC<ApiStatusProps> = ({ 
  showDetails = false, 
  className = '',
  variant = 'full',
  position = 'inline'
}) => {
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(showDetails);

  const configStatus = getConfigurationStatus() || { configured: true, warnings: [], environment: 'test' };

  useEffect(() => {
    if (expanded || variant === 'pill' || variant === 'minimal') {
      checkHealth();
    }
  }, [expanded, variant]);

  const checkHealth = async () => {
    setLoading(true);
    try {
      const status = await ApiHealthService.checkHealth();
      setHealthStatus(status);
    } catch (error) {
      setHealthStatus({
        healthy: false,
        configured: configStatus.configured,
        message: 'Failed to check API health',
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    if (!configStatus.configured) {
      return '⚠️';
    }
    if (healthStatus === null) {
      return '❓';
    }
    return healthStatus.healthy ? '✅' : '❌';
  };

  const getStatusText = () => {
    if (!configStatus.configured) {
      return 'Not Configured';
    }
    if (healthStatus === null) {
      return 'Unknown';
    }
    return healthStatus.healthy ? 'Healthy' : 'Unhealthy';
  };

  const getStatusColor = () => {
    if (!configStatus.configured) {
      return 'warning';
    }
    if (healthStatus === null) {
      return 'unknown';
    }
    return healthStatus.healthy ? 'success' : 'error';
  };

  // Render different variants
  if (variant === 'pill') {
    return (
      <div className={`api-status-pill ${getStatusColor()} ${className}`}>
        <span className="status-icon-small">{getStatusIcon()}</span>
        <span className="status-text-small">API</span>
      </div>
    );
  }

  if (variant === 'minimal') {
    return (
      <div className={`api-status-minimal ${getStatusColor()} ${className}`}>
        <span className="status-icon-small">{getStatusIcon()}</span>
      </div>
    );
  }

  if (variant === 'contextual') {
    return (
      <div className={`api-status-contextual ${getStatusColor()} ${className}`}>
        <span className="status-icon-small">{getStatusIcon()}</span>
        <span className="status-text-contextual">
          {!configStatus.configured ? 'API Not Configured' : 
           healthStatus === null ? 'API Status Unknown' :
           healthStatus.healthy ? 'API Ready' : 'API Issues'}
        </span>
        {!configStatus.configured && (
          <span className="status-action">Configure</span>
        )}
      </div>
    );
  }

  return (
    <div className={`api-status ${className}`}>
      <div 
        className={`api-status-summary ${getStatusColor()}`}
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: 'pointer' }}
      >
        <span className="status-icon">{getStatusIcon()}</span>
        <span className="status-text">API: {getStatusText()}</span>
        {!showDetails && (
          <span className="expand-icon">{expanded ? '▼' : '▶'}</span>
        )}
      </div>

      {expanded && (
        <div className="api-status-details">
          <div className="status-section">
            <h4>Configuration</h4>
            <div className="status-item">
              <span className="label">Environment:</span>
              <span className="value">{configStatus.environment}</span>
            </div>
            <div className="status-item">
              <span className="label">Configured:</span>
              <span className={`value ${configStatus.configured ? 'success' : 'error'}`}>
                {configStatus.configured ? 'Yes' : 'No'}
              </span>
            </div>
            {configStatus.warnings.length > 0 && (
              <div className="status-warnings">
                <span className="label">Warnings:</span>
                <ul>
                  {configStatus.warnings.map((warning, index) => (
                    <li key={index} className="warning-item">{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {configStatus.configured && (
            <div className="status-section">
              <h4>Health Check</h4>
              <div className="health-check-controls">
                <button 
                  className="btn btn-small"
                  onClick={checkHealth}
                  disabled={loading}
                >
                  {loading ? 'Checking...' : 'Check Health'}
                </button>
              </div>
              
              {healthStatus && (
                <>
                  <div className="status-item">
                    <span className="label">Status:</span>
                    <span className={`value ${healthStatus.healthy ? 'success' : 'error'}`}>
                      {healthStatus.healthy ? 'Healthy' : 'Unhealthy'}
                    </span>
                  </div>
                  <div className="status-item">
                    <span className="label">Message:</span>
                    <span className="value">{healthStatus.message}</span>
                  </div>
                  <div className="status-item">
                    <span className="label">Last Check:</span>
                    <span className="value">
                      {new Date(healthStatus.timestamp).toLocaleString()}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          {!configStatus.configured && (
            <div className="status-section">
              <h4>Setup Instructions</h4>
              <div className="setup-instructions">
                <p>To configure the API connection:</p>
                <ol>
                  <li>Deploy the backend infrastructure using <code>sam deploy</code></li>
                  <li>Copy the API Gateway URL from the deployment output</li>
                  <li>Update the <code>REACT_APP_API_URL</code> environment variable</li>
                  <li>Restart the development server</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApiStatus;